import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { motion, AnimatePresence } from "framer-motion";
import {
  Play, Pause, RotateCcw, Mic, Sparkles, Code2, Terminal, Image as ImageIcon,
  Video, Share2, Camera, Languages, FileText, Check, Copy, Volume2, Globe, Send,
} from "lucide-react";

/* An in-app, cinematic animated screencast that plays inside a lightbox —
   a stylised "product demo video" showcasing EVERYTHING Sara can do:
   sites web, applications & Python, images & vidéos IA, automatisation
   réseaux sociaux, vision caméra temps réel, multilingue, lecture de PDF. */

const SCENES = [
  { id: "intro", dur: 3000 },
  { id: "web", dur: 4400 },
  { id: "app", dur: 4200 },
  { id: "media", dur: 4200 },
  { id: "social", dur: 4200 },
  { id: "vision", dur: 4200 },
  { id: "multilang", dur: 3800 },
  { id: "pdf", dur: 4000 },
  { id: "outro", dur: 3800 },
];
const TOTAL = SCENES.reduce((a, s) => a + s.dur, 0);

const CODE_LINES = [
  "<!doctype html>",
  '<html lang="fr">',
  "  <head><title>Café Lumière</title></head>",
  "  <body>",
  '    <header class="hero">',
  "      <h1>Café Lumière</h1>",
  "      <a class=\"cta\">Réserver</a>",
  "    </header>",
  "  </body>",
  "</html>",
];
const PY_LINES = [
  "from fastapi import FastAPI",
  "app = FastAPI()",
  "",
  "@app.get('/ventes')",
  "def ventes():",
  "    data = charger_csv('ventes.csv')",
  "    return resume(data)",
];

/* ---- shared chrome ---- */
function Frame({ url, children }) {
  return (
    <div className="flex h-full w-full flex-col overflow-hidden rounded-xl border border-white/10 bg-[#0b0f17]">
      <div className="flex items-center gap-2 border-b border-white/10 bg-[#11161f] px-3 py-2">
        <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f56]" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#ffbd2e]" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#27c93f]" />
        <div className="ml-3 flex-1 truncate rounded-md bg-white/[0.06] px-3 py-1 font-mono text-[11px] text-slate-400">
          {url}
        </div>
      </div>
      <div className="relative flex-1 overflow-hidden">{children}</div>
    </div>
  );
}

function Cmd({ text }) {
  return (
    <div className="flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-400/[0.07] px-3 py-1.5">
      <Mic className="h-3.5 w-3.5 shrink-0 text-cyan-300" />
      <span className="truncate text-[12px] text-cyan-50/90 sm:text-[13px]">«&nbsp;{text}&nbsp;»</span>
    </div>
  );
}

function SceneShell({ icon: Icon, tag, cmd, children }) {
  return (
    <div className="flex h-full flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg border border-cyan-400/40 bg-cyan-400/10 text-cyan-300">
            <Icon className="h-4 w-4" />
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-slate-400">{tag}</span>
        </div>
        <Cmd text={cmd} />
      </div>
      <div className="min-h-0 flex-1">{children}</div>
    </div>
  );
}

function Typewriter({ text, speed = 55, active }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!active) return;
    setN(0);
    const t = setInterval(() => setN((v) => (v < text.length ? v + 1 : v)), speed);
    return () => clearInterval(t);
  }, [text, speed, active]);
  return (
    <span>
      {text.slice(0, n)}
      <span className="ml-0.5 inline-block h-3.5 w-[2px] translate-y-0.5 animate-pulse bg-cyan-300" />
    </span>
  );
}

/* ---- scenes ---- */
function IntroScene() {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-4 text-center">
      <motion.span
        initial={{ scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="flex h-16 w-16 items-center justify-center rounded-full border border-cyan-400/40 bg-cyan-400/10"
      >
        <Sparkles className="h-7 w-7 text-cyan-300" />
      </motion.span>
      <motion.div initial={{ y: 16, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2, duration: 0.6 }}>
        <div className="text-2xl font-black tracking-[0.3em] text-white">SARA</div>
        <div className="mt-2 text-sm text-slate-400">Donnez l'ordre. Sara exécute — tout, à la voix.</div>
      </motion.div>
    </div>
  );
}

function CodeTyper({ url, file, lines, active, color = "#9ecbff" }) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!active) return;
    setCount(0);
    const t = setInterval(() => setCount((v) => (v < lines.length ? v + 1 : v)), 240);
    return () => clearInterval(t);
  }, [active, lines]);
  return (
    <Frame url={url}>
      <div className="flex items-center justify-between border-b border-white/10 bg-[#11161f] px-3 py-1.5">
        <span className="font-mono text-[11px] text-slate-400">{file}</span>
        <span className="flex items-center gap-1 rounded-md border border-white/10 bg-white/5 px-2 py-0.5 font-mono text-[10px] text-slate-300">
          <Copy className="h-3 w-3" /> Copier
        </span>
      </div>
      <div className="h-full overflow-hidden p-3 font-mono text-[12px] leading-relaxed">
        {lines.slice(0, count).map((l, i) => (
          <motion.div key={i} initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} className="flex gap-3">
            <span className="w-5 select-none text-right text-[#3a4658]">{i + 1}</span>
            <span className="whitespace-pre" style={{ color }}>{l}</span>
          </motion.div>
        ))}
      </div>
    </Frame>
  );
}

function WebScene({ active }) {
  return (
    <SceneShell icon={Globe} tag="Sites web" cmd="Sara, crée un site pour mon café">
      <div className="grid h-full grid-cols-1 gap-3 sm:grid-cols-2">
        <CodeTyper url="studio.sara.ai — Café Lumière" file="index.html" lines={CODE_LINES} active={active} />
        <Frame url="cafe-lumiere.fr">
          <div className="relative flex h-full flex-col items-center justify-center gap-2 bg-gradient-to-br from-[#2a1a12] via-[#3d2417] to-[#1a1008] text-center">
            <div className="text-2xl font-black tracking-wide text-amber-50">Café Lumière</div>
            <div className="text-[11px] text-amber-200/80">Torréfaction artisanale · Lyon</div>
            <div className="mt-1 rounded-full bg-amber-400 px-4 py-1.5 text-[12px] font-semibold text-[#2a1a12]">Réserver</div>
            <motion.div
              initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.7, type: "spring" }}
              className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-emerald-500/90 px-2.5 py-1 text-[10px] font-semibold text-white"
            >
              <Check className="h-3 w-3" /> 12 s
            </motion.div>
          </div>
        </Frame>
      </div>
    </SceneShell>
  );
}

function AppScene({ active }) {
  return (
    <SceneShell icon={Terminal} tag="Applications & Python" cmd="Sara, code une API qui résume mes ventes">
      <div className="grid h-full grid-cols-1 gap-3 sm:grid-cols-[1.3fr_1fr]">
        <CodeTyper url="studio.sara.ai — api" file="main.py" lines={PY_LINES} active={active} color="#c3e88d" />
        <div className="flex h-full flex-col overflow-hidden rounded-xl border border-white/10 bg-[#0b0f17]">
          <div className="border-b border-white/10 bg-[#11161f] px-3 py-1.5 font-mono text-[11px] text-slate-400">terminal</div>
          <div className="flex-1 space-y-1 p-3 font-mono text-[11px] leading-relaxed text-slate-300">
            <div className="text-slate-500">$ uvicorn main:app --reload</div>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}>Application startup complete.</motion.div>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }} className="text-emerald-400">
              ✓ GET /ventes 200 OK
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.8 }}
              className="mt-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-2 text-[11px] text-emerald-200">
              CA du mois : <b>48 210 €</b> · +12 % · top produit : Espresso
            </motion.div>
          </div>
        </div>
      </div>
    </SceneShell>
  );
}

function MediaScene({ active }) {
  return (
    <SceneShell icon={ImageIcon} tag="Images & Vidéos IA" cmd="Sara, génère une image et une vidéo de mon produit">
      <div className="grid h-full grid-cols-2 gap-3">
        <div className="relative overflow-hidden rounded-xl border border-white/10">
          <div className="absolute inset-0 bg-gradient-to-br from-fuchsia-500/40 via-cyan-400/30 to-violet-600/40" />
          {active && (
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/25 to-transparent"
              animate={{ x: ["-120%", "120%"] }}
              transition={{ repeat: Infinity, duration: 1.6, ease: "linear" }}
            />
          )}
          <div className="absolute left-2 top-2 flex items-center gap-1 rounded-full bg-black/50 px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-white">
            <ImageIcon className="h-3 w-3" /> Image
          </div>
          <div className="absolute bottom-2 left-2 right-2 text-[10px] text-white/90">Rendu 1024×1024 · nano-banana</div>
        </div>
        <div className="relative overflow-hidden rounded-xl border border-white/10 bg-[#0b0f17]">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0e2a3a] to-[#1a1030]" />
          <div className="absolute inset-0 flex items-center justify-center">
            <motion.span
              animate={{ scale: [1, 1.12, 1] }} transition={{ repeat: Infinity, duration: 1.4 }}
              className="flex h-12 w-12 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur"
            >
              <Play className="h-5 w-5 translate-x-[1px]" />
            </motion.span>
          </div>
          <div className="absolute left-2 top-2 flex items-center gap-1 rounded-full bg-black/50 px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-white">
            <Video className="h-3 w-3" /> Vidéo
          </div>
          <div className="absolute bottom-3 left-3 right-3">
            <div className="h-1.5 overflow-hidden rounded-full bg-white/15">
              <motion.div className="h-full rounded-full bg-gradient-to-r from-cyan-400 to-fuchsia-400"
                initial={{ width: "5%" }} animate={active ? { width: "100%" } : {}} transition={{ duration: 3 }} />
            </div>
            <div className="mt-1 font-mono text-[9px] text-white/70">Rendu vidéo…</div>
          </div>
        </div>
      </div>
    </SceneShell>
  );
}

function SocialScene({ active }) {
  const rows = [
    { tag: "IG", name: "Instagram", color: "#e1306c", txt: "Réel + légende + hashtags" },
    { tag: "X", name: "X / Twitter", color: "#38bdf8", txt: "Thread en 4 posts" },
    { tag: "in", name: "LinkedIn", color: "#0a66c2", txt: "Article pro programmé" },
  ];
  return (
    <SceneShell icon={Share2} tag="Automatisation réseaux sociaux" cmd="Sara, publie ma promo sur tous mes réseaux">
      <div className="flex h-full flex-col justify-center gap-2.5">
        {rows.map((r, i) => (
          <motion.div
            key={r.name}
            initial={{ opacity: 0, x: -12 }} animate={active ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.4 + i * 0.5 }}
            className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2.5"
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-[12px] font-black text-white" style={{ background: r.color }}>
              {r.tag}
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-semibold text-white">{r.name}</div>
              <div className="truncate text-[11px] text-slate-400">{r.txt}</div>
            </div>
            <motion.span
              initial={{ scale: 0 }} animate={active ? { scale: 1 } : {}} transition={{ delay: 0.9 + i * 0.5, type: "spring" }}
              className="flex items-center gap-1 rounded-full bg-emerald-500/90 px-2.5 py-1 text-[10px] font-semibold text-white"
            >
              <Check className="h-3 w-3" /> Programmé
            </motion.span>
          </motion.div>
        ))}
      </div>
    </SceneShell>
  );
}

function VisionScene({ active }) {
  return (
    <SceneShell icon={Camera} tag="Vision temps réel — caméra" cmd="Sara, regarde et dis-moi ce que tu vois">
      <Frame url="caméra · analyse en direct">
        <div className="relative h-full w-full bg-gradient-to-br from-[#0a1420] to-[#0e1b12]">
          {/* corner brackets */}
          {[
            "left-3 top-3 border-l-2 border-t-2", "right-3 top-3 border-r-2 border-t-2",
            "left-3 bottom-3 border-l-2 border-b-2", "right-3 bottom-3 border-r-2 border-b-2",
          ].map((c) => (
            <span key={c} className={`absolute h-6 w-6 border-cyan-300/80 ${c}`} />
          ))}
          {active && (
            <motion.div
              className="absolute inset-x-3 h-[2px] bg-cyan-300/80"
              style={{ boxShadow: "0 0 14px rgba(56,189,248,0.9)" }}
              animate={{ top: ["12%", "84%", "12%"] }}
              transition={{ repeat: Infinity, duration: 2.6, ease: "easeInOut" }}
            />
          )}
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={active ? { opacity: 1, scale: 1 } : {}} transition={{ delay: 0.8 }}
            className="absolute left-[18%] top-[30%] rounded-md border border-cyan-300/70 bg-black/50 px-2 py-0.5 font-mono text-[10px] text-cyan-100">
            Tasse · 96%
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={active ? { opacity: 1, scale: 1 } : {}} transition={{ delay: 1.3 }}
            className="absolute right-[16%] top-[52%] rounded-md border border-emerald-300/70 bg-black/50 px-2 py-0.5 font-mono text-[10px] text-emerald-100">
            Ordinateur · 93%
          </motion.div>
          <div className="absolute inset-x-3 bottom-3 rounded-lg border border-white/10 bg-black/55 px-3 py-2 text-[12px] text-cyan-50 backdrop-blur">
            <span className="mr-2 font-mono text-[9px] uppercase tracking-wider text-cyan-300">Sara</span>
            Je vois une tasse de café à côté d'un ordinateur portable sur un bureau clair.
          </div>
        </div>
      </Frame>
    </SceneShell>
  );
}

function MultiLangScene({ active }) {
  const greetings = ["Bonjour", "Hello", "Hola", "مرحبا", "你好", "こんにちは", "Ciao"];
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (!active) return;
    setIdx(0);
    const t = setInterval(() => setIdx((v) => (v + 1) % greetings.length), 520);
    return () => clearInterval(t);
  }, [active]); // eslint-disable-line
  return (
    <SceneShell icon={Languages} tag="Multilingue" cmd="Sara, parle-moi dans ma langue">
      <div className="flex h-full flex-col items-center justify-center gap-5">
        <motion.span
          animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 12, ease: "linear" }}
          className="flex h-16 w-16 items-center justify-center rounded-full border border-cyan-400/40 bg-cyan-400/10 text-cyan-300"
        >
          <Globe className="h-8 w-8" />
        </motion.span>
        <div className="h-10 text-center">
          <AnimatePresence mode="wait">
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
              className="text-3xl font-black text-white"
            >
              {greetings[idx]}
            </motion.div>
          </AnimatePresence>
        </div>
        <div className="flex items-end gap-1">
          {[12, 22, 16, 28, 18, 24, 14, 20].map((h, i) => (
            <motion.span key={i} className="w-1 rounded-full bg-cyan-400/70"
              animate={{ height: [h, h * 1.8, h] }} transition={{ repeat: Infinity, duration: 0.7, delay: i * 0.07 }} style={{ height: h }} />
          ))}
        </div>
        <div className="text-[11px] text-slate-400">FR · EN · ES · AR · ZH · JA · IT — détection & réponse automatiques</div>
      </div>
    </SceneShell>
  );
}

function PdfScene({ active }) {
  return (
    <SceneShell icon={FileText} tag="Lecture de PDF" cmd="Sara, résume ce PDF de 40 pages">
      <div className="grid h-full grid-cols-2 gap-3">
        <div className="relative overflow-hidden rounded-xl border border-white/10 bg-white/[0.03] p-3">
          <div className="mb-2 flex items-center gap-2 text-[11px] text-slate-400">
            <FileText className="h-3.5 w-3.5 text-rose-300" /> rapport-annuel.pdf
          </div>
          <div className="space-y-1.5">
            {[100, 92, 96, 78, 88, 94, 70, 90, 84].map((w, i) => (
              <div key={i} className="h-2 rounded bg-white/10" style={{ width: `${w}%` }} />
            ))}
          </div>
          {active && (
            <motion.div
              className="absolute inset-x-3 h-6 rounded bg-cyan-300/20"
              style={{ boxShadow: "0 0 18px rgba(56,189,248,0.5)" }}
              animate={{ top: ["16%", "82%", "16%"] }}
              transition={{ repeat: Infinity, duration: 2.8, ease: "easeInOut" }}
            />
          )}
        </div>
        <div className="flex flex-col overflow-hidden rounded-xl border border-cyan-400/25 bg-cyan-400/[0.05] p-3">
          <div className="mb-2 flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-cyan-300">
            <Sparkles className="h-3.5 w-3.5" /> Résumé de Sara
          </div>
          {["Chiffre d'affaires en hausse de 18 % sur l'année.", "3 marchés porteurs identifiés : FR, DE, ES.", "Recommandation : renforcer le support client."].map((t, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 6 }} animate={active ? { opacity: 1, y: 0 } : {}} transition={{ delay: 0.6 + i * 0.6 }}
              className="mb-2 flex items-start gap-2 text-[12px] leading-snug text-cyan-50/90"
            >
              <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-400" /> {t}
            </motion.div>
          ))}
        </div>
      </div>
    </SceneShell>
  );
}

function OutroScene() {
  const chips = ["Sites web", "Applications", "Python", "Images", "Vidéos", "Réseaux sociaux", "Vision caméra", "Multilingue", "PDF"];
  return (
    <div className="flex h-full flex-col items-center justify-center gap-4 text-center">
      <motion.div initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", duration: 0.7 }}>
        <div className="bg-gradient-to-r from-cyan-300 via-white to-violet-300 bg-clip-text text-2xl font-black tracking-tight text-transparent sm:text-3xl">
          Vous pouvez tout faire avec Sara
        </div>
      </motion.div>
      <div className="flex max-w-lg flex-wrap items-center justify-center gap-1.5">
        {chips.map((c, i) => (
          <motion.span
            key={c}
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 + i * 0.08 }}
            className="rounded-full border border-white/12 bg-white/[0.05] px-2.5 py-1 text-[11px] text-slate-200"
          >
            {c}
          </motion.span>
        ))}
      </div>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }}
        className="mt-1 flex items-center gap-2 rounded-full bg-gradient-to-r from-cyan-500 to-violet-500 px-5 py-2 text-sm font-semibold text-white"
      >
        <Send className="h-4 w-4" /> Donnez votre premier ordre
      </motion.div>
    </div>
  );
}

const RENDER = {
  intro: IntroScene, web: WebScene, app: AppScene, media: MediaScene,
  social: SocialScene, vision: VisionScene, multilang: MultiLangScene, pdf: PdfScene, outro: OutroScene,
};

export function DemoVideoModal({ open, onClose }) {
  const [step, setStep] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [elapsed, setElapsed] = useState(0);
  const raf = useRef(0);
  const last = useRef(0);
  const acc = useRef(0);

  useEffect(() => {
    if (open) {
      setStep(0);
      setElapsed(0);
      acc.current = 0;
      setPlaying(true);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !playing) return;
    last.current = performance.now();
    const tick = (now) => {
      const dt = now - last.current;
      last.current = now;
      acc.current += dt;
      let total = acc.current;
      if (total >= TOTAL) {
        acc.current = 0;
        total = 0;
      }
      let cum = 0;
      let idx = 0;
      for (let i = 0; i < SCENES.length; i++) {
        if (total < cum + SCENES[i].dur) {
          idx = i;
          break;
        }
        cum += SCENES[i].dur;
      }
      setStep(idx);
      setElapsed(total);
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [open, playing]);

  const restart = () => {
    acc.current = 0;
    setElapsed(0);
    setStep(0);
    setPlaying(true);
  };

  const sceneId = SCENES[step].id;
  const Scene = RENDER[sceneId];
  const progress = Math.min(100, (elapsed / TOTAL) * 100);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="demo-video-modal"
        className="max-w-3xl border border-white/10 bg-[#05060e] p-0 text-white sm:rounded-2xl"
      >
        <DialogTitle className="sr-only">Démo de Sara</DialogTitle>
        <DialogDescription className="sr-only">
          Aperçu animé de tout ce que Sara peut faire : sites, apps, images, vidéos, réseaux sociaux, vision, multilingue, PDF.
        </DialogDescription>
        <div className="flex items-center justify-between border-b border-white/10 px-4 py-2.5">
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 animate-pulse rounded-full bg-rose-500" />
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400">Sara · Démo complète</span>
          </div>
          <span className="flex items-center gap-1 font-mono text-[10px] text-slate-500">
            <Volume2 className="h-3.5 w-3.5" /> screencast
          </span>
        </div>

        {/* stage */}
        <div className="relative aspect-video w-full overflow-hidden bg-[#080b12] p-4 sm:p-6">
          <AnimatePresence mode="wait">
            <motion.div
              key={sceneId}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.4 }}
              className="h-full w-full"
            >
              <Scene active />
            </motion.div>
          </AnimatePresence>
        </div>

        {/* controls */}
        <div className="flex items-center gap-3 border-t border-white/10 px-4 py-3">
          <button
            type="button"
            data-testid="demo-play-toggle"
            onClick={() => {
              last.current = performance.now();
              setPlaying((p) => !p);
            }}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
          >
            {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
          </button>
          <button
            type="button"
            data-testid="demo-restart"
            onClick={restart}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-white/5 text-slate-300 transition-colors hover:bg-white/15"
          >
            <RotateCcw className="h-4 w-4" />
          </button>
          <div className="relative h-1.5 flex-1 overflow-hidden rounded-full bg-white/10">
            <div
              className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-cyan-400 to-violet-400"
              style={{ width: `${progress}%` }}
            />
          </div>
          <span className="font-mono text-[10px] text-slate-500">{Math.round(progress)}%</span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
